import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Alert, Button, DateField, Page, PageHeader, Select } from '@evogard/ui';
import {
  RETENTION_PHASE_LABELS,
  businessDate,
  type RetentionPhase,
} from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { useRetentionTenants } from './useRetention.js';

/** As colunas do relatório do legado (`CAMPOS_RELATORIO`), na mesma ordem. */
const COLUMNS: Array<[string, string]> = [
  ['memberName', 'Nome'],
  ['document', 'CPF'],
  ['email', 'E-mail'],
  ['phone', 'Telefone'],
  ['plate', 'Placa'],
  ['tenant', 'Base'],
  ['vehicleStatus', 'Situação do veículo'],
  ['fixedAmount', 'Valor fixo'],
  ['reasonText', 'Motivo'],
  ['consultantName', 'Consultor'],
  ['consultantEmail', 'E-mail consultor'],
  ['consultantPhone', 'Telefone consultor'],
  ['phase', 'Fase'],
  ['operator', 'Operador'],
  ['firstOperator', 'Primeiro atendente'],
  ['handledBy', 'Atendente que tratou'],
  ['ourNumber', 'Nosso número'],
  ['chatProtocol', 'Protocolo do chat'],
  ['handling', 'Tratativa com cliente'],
  ['lastContactOn', 'Data de último contato'],
  ['notes', 'Observação'],
  ['labels', 'Etiquetas'],
  ['createdAt', 'Entrada no kanban'],
  ['phaseSince', 'Na fase desde'],
  ['retainedIn72h', 'Retido a partir das 72h'],
  ['boletoLink', 'Boleto (link)'],
  ['attachments', 'Arquivos'],
  ['protocol', 'Protocolo'],
];

function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * O relatório do Kanban Cancelamento — o Excel da aba "Relatórios" do legado,
 * em CSV, pelo dia de entrada no quadro.
 */
export function ReportScreen() {
  const tenants = useRetentionTenants();
  const { data: context } = useSessionContext();
  const [tenant, setTenant] = useState('');
  useEffect(() => {
    if (!tenant && context) setTenant(context.currentTenant.slug);
  }, [tenant, context]);
  const today = businessDate(new Date());
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);

  const download = useMutation({
    mutationFn: async () =>
      ok(
        await api.retention.report({ params: { tenant }, query: { from, to } }),
        200,
        'Não foi possível gerar o relatório.',
      ),
    onSuccess: (r) => {
      const lines = [
        COLUMNS.map(([, label]) => csvCell(label)).join(';'),
        ...r.items.map((row) =>
          COLUMNS.map(([key]) => {
            const v = (row as Record<string, unknown>)[key];
            if (key === 'phase')
              return csvCell(RETENTION_PHASE_LABELS[v as RetentionPhase]);
            if (key === 'retainedIn72h') return csvCell(v ? 'Sim' : 'Não');
            return csvCell(v);
          }).join(';'),
        ),
      ];
      const blob = new Blob([`\uFEFF${lines.join('\n')}`], {
        type: 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kanban-cancelamento-${tenant}-${from}-a-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });

  return (
    <Page>
      <PageHeader title="Relatório">
        Os pedidos que entraram no quadro no período, com as colunas do relatório do
        sistema antigo.
      </PageHeader>
      <div className="flex flex-wrap items-end gap-3">
        <Select label="Base" value={tenant} onChange={(e) => setTenant(e.target.value)}>
          {tenants.map((t) => (
            <option key={t.slug} value={t.slug}>
              {t.name}
            </option>
          ))}
        </Select>
        <DateField label="Entrada de" value={from} onChange={setFrom} />
        <DateField label="até" value={to} onChange={setTo} />
        <Button
          loading={download.isPending}
          disabled={!tenant}
          onClick={() => download.mutate()}
        >
          Baixar CSV
        </Button>
      </div>
      {download.isError && <Alert tone="erro">{download.error.message}</Alert>}
      {download.isSuccess && (
        <Alert tone="sucesso">{download.data.items.length} pedido(s) no arquivo.</Alert>
      )}
    </Page>
  );
}
